import { delay } from '../../../../utils/TimeUtils'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { checkACK, versionFileName } from '../../capabilities/memory'
import { CMD_CH585M, PROTOCOL_CONFIG } from '../../protocols/cmd'
import {
  CMD_IAP_PROM,
  CMD_IAP_ERASE,
  CMD_IAP_VERIFY,
  CMD_IAP_END,
  CMD_IAP_FW_INFO,
  SINGLE_PID_LIST,
} from '../../protocols/cmd'
import { processZip } from '../../../shared/firmware/shared/FileUtil'
import {
  RemoteDeviceVersion,
  VersionInfo,
  VersionType,
  DeviceType,
} from '../../../../types/firmware'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { formatCompactFirmwareVersion } from '../../../../utils/verify'

// 数据包大小
const PACKET_SIZE = 64
const DATA_CHUNK = 0x30 // 每包有效数据 48 字节

const REPORT_ID = PROTOCOL_CONFIG.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG.feature_report_id

export class CH585MFirmware extends FirmwareFeature {
  private firmwareBuffer: Uint8Array = new Uint8Array(0)
  private startAddress: number = 0x00003000
  private lastResponse: Uint8Array | null = null
  private updateDevice: BaseDevice | null = null

  // ========================= 公开接口 =========================

  async getFirmwareVersion(): Promise<VersionInfo[]> {
    const cmd = CMD_CH585M.firmware_version[0]
    const payload = new Uint8Array([this.device.connectionType, cmd, 0])
    const res = await this.device.sendAndWait(
      REPORT_ID,
      payload,
      FEATURE_REPORT_ID,
      (res) => res.getUint8(0) !== 0x02,
    )
    checkACK(res)
    const version = res.getUint32(1, true)
    const versionStr = formatCompactFirmwareVersion(version)
    const versionInfo = {
      version: versionStr,
      versionType: VersionType.MasterControl,
      deviceType: DeviceType.Keyboard,
    }
    return [versionInfo]
  }

  private async getVersionInfo(): Promise<RemoteDeviceVersion> {
    const model = this.device.manifest.mainControl ?? ''
    const timestamp = Date.now()
    const versionUrl = `${this.device.getFirmwareBaseUrl()}/${model}/${versionFileName}?t=${timestamp}`
    const versionRes = await fetch(versionUrl)
    if (!versionRes.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, 'checkFirmwareUpdate failed')
    }
    const versionData = await versionRes.json()
    if (!versionData.Device || !versionData.Device.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'checkFirmwareUpdate failed')
    }
    return versionData.Device[0]
  }

  async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()

    // 这里写死，因为CH585M目前只有一个芯片要更新
    const masterControlVersion = versions[0]?.version
    if (!masterControlVersion) {
      return versions.map((v) => ({ ...v, canUpdate: false }))
    }
    const versionFileInfo = await this.getVersionInfo()
    const version = versionFileInfo.FirmwareVersion
    if (!version) {
      return versions.map((v) => ({ ...v, canUpdate: false }))
    }
    const canUpdate = this.canUpdateByVersion(version, masterControlVersion)

    return versions.map((v) => ({ ...v, onlineVersion: version, canUpdate }))
  }

  async updateFirmware(): Promise<void> {
    try {
      const versionFileInfo = await this.getVersionInfo()
      const FirmwareURL = versionFileInfo.FirmwareURL
      if (!FirmwareURL) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
      }

      const model = this.device.manifest.mainControl ?? ''
      const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
      const fileMap = await processZip(url)
      const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array
      if (!firmwareData) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'firmwareData is empty')
      }
      this.firmwareBuffer = firmwareData

      this.updateDevice = await this.device.session.channels.openDevice({ usageType: 'update' })

      if (!this.updateDevice) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateDeivce is empty')
      }

      const isSingle = this.isSingleAreaDevice()
      if (isSingle) {
        await this.enterBootLoader()
        this.reportProgress(1)
      }

      if (!isSingle) {
        // 双区握手（通常设备会重启，应答可能丢失，直接忽略）
        await this.startDeviceUpdate()
      } else {
        await this.deviceUpdateCheck()
      }
      this.reportProgress(2)

      await this.updateFirmwareIndex(1)
      this.reportProgress(3)

      if (!isSingle) {
        await this.eraseDeviceFlash()
      } else {
        await this.eraseDeviceFlashNew()
      }
      this.reportProgress(5)

      await this.writeDeviceFlashData()
      await this.checkDeviceFlashData()
      await this.endDeviceUpdate()

      this.reportProgress(100)
    } catch (error) {
      console.error(error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, JSON.stringify(error))
    } finally {
      await this.device.session.channels.releaseDevice(this.updateDevice)
      this.updateDevice = null
    }
  }

  // ============== 私有协议方法 ==============

  private async deviceUpdateCheck(): Promise<void> {
    console.log('[CH585M] device update check')
    const pid = this.device.channel.deviceInfo?.productId ?? 0
    const buf = new Uint8Array(PACKET_SIZE)
    buf[0] = 0x5a
    buf[1] = 0x02
    buf[2] = pid & 0xff
    buf[3] = (pid >> 8) & 0xff
    buf[4] = this.calcChecksum(buf.slice(0, 4))

    await this.sendRawPacket(buf)
    if (!this.lastResponse) {
      // 重试一次
      await this.sendRawPacket(buf)
      if (!this.lastResponse) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'deviceUpdateCheck no response after retry')
      }
    }
    if (this.lastResponse![2] === 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Device update check failed')
    }
  }

  // 1代表键盘设备 2代表Dongle设备
  private async updateFirmwareIndex(index = 1): Promise<void> {
    console.log(`[CH585M] update firmware index: ${index}`)
    const buf = new Uint8Array(PACKET_SIZE)
    buf[0] = CMD_IAP_FW_INFO
    buf[1] = 0x04
    buf[2] = index
    buf[6] = this.calcChecksum(buf.slice(0, 6))

    await this.sendRawPacket(buf)
    if (!this.lastResponse) {
      await this.sendRawPacket(buf)
      if (!this.lastResponse) {
        throw new DeviceError(
          DeviceErrorCode.TIMEOUT,
          'updateFirmwareIndex no response after retry',
        )
      }
    }
    if (this.lastResponse![2] === 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Update firmware index failed')
    }
  }

  private async eraseDeviceFlash(): Promise<void> {
    console.log('[CH585M] erase device flash')
    const info = this.lastResponse
    if (!info) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'No previous response for erase params')
    }
    const dv = new DataView(info.buffer, info.byteOffset, info.byteLength)
    const blockSize = dv.getUint16(6, true)
    const totalSize = dv.getUint32(2, true)
    const count = totalSize / blockSize
    this.startAddress = blockSize / 16

    const buf = new Uint8Array(PACKET_SIZE)
    buf[0] = CMD_IAP_ERASE
    buf[1] = 0x04
    buf[2] = this.startAddress & 0xff
    buf[3] = (this.startAddress >> 8) & 0xff
    buf[4] = count & 0xff
    buf[5] = (count >> 8) & 0xff
    buf[6] = this.calcChecksum(buf.slice(0, 6))

    await this.sendRawPacket(buf)
    if (!this.lastResponse) {
      await this.sendRawPacket(buf)
      if (!this.lastResponse) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'Erase flash no response after retry')
      }
    }
    if (this.lastResponse![2] === 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Erase flash failed (dual area)')
    }
  }

  private async eraseDeviceFlashNew(): Promise<void> {
    console.log('[CH585M] erase device flash new')
    const info = this.lastResponse
    if (!info) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'No previous response for erase params')
    }
    const dv = new DataView(info.buffer, info.byteOffset, info.byteLength)
    const address = dv.getUint32(2, true)
    const totalSize = dv.getUint32(6, true)
    const blockSize = dv.getUint16(10, true)
    const count = totalSize / blockSize
    this.startAddress = address / 16

    const buf = new Uint8Array(PACKET_SIZE)
    buf[0] = CMD_IAP_ERASE
    buf[1] = 0x04
    buf[2] = this.startAddress & 0xff
    buf[3] = (this.startAddress >> 8) & 0xff
    buf[4] = count & 0xff
    buf[5] = (count >> 8) & 0xff
    buf[6] = this.calcChecksum(buf.slice(0, 6))

    await this.sendRawPacket(buf)
    if (!this.lastResponse) {
      await this.sendRawPacket(buf)
      if (!this.lastResponse) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'Erase flash new no response after retry')
      }
    }
    if (this.lastResponse![2] === 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Erase flash failed (single area)')
    }
  }

  private async writeDeviceFlashData(): Promise<void> {
    console.log('[CH585M] write device flash data')
    const total = this.firmwareBuffer.length
    let offset = 0
    let addr = this.startAddress

    while (offset < total) {
      let chunkSize = DATA_CHUNK
      if (offset + DATA_CHUNK > total) {
        chunkSize = total - offset
        if (chunkSize === DATA_CHUNK) {
          return this.endWriteDeviceFlashData(total)
        }
      }

      const chunk = this.firmwareBuffer.slice(offset, offset + chunkSize)
      const buf = new Uint8Array(PACKET_SIZE)
      buf[0] = CMD_IAP_PROM
      buf[1] = chunkSize + 2
      buf[2] = addr & 0xff
      buf[3] = (addr >> 8) & 0xff
      buf.set(chunk, 4)
      buf[4 + chunkSize] = this.calcChecksum(buf.slice(0, 4 + chunkSize))

      await this.sendRawPacket(buf)
      // 写入时允许单包超时（跳过检查，继续下一包）
      if (this.lastResponse === null) {
        console.warn(`Write packet at offset ${offset} timeout, skipping check`)
      } else if (this.lastResponse[2] === 0xff) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Write flash data failed')
      }

      offset += chunkSize
      addr += chunkSize / 16
      this.reportProgress(5 + Math.floor((offset / total) * 45))
    }

    this.reportProgress(50)
  }

  private async endWriteDeviceFlashData(length: number): Promise<void> {
    console.log('[CH585M] end write device flash data')
    const buf = new Uint8Array(PACKET_SIZE)
    buf[1] = CMD_IAP_PROM
    buf[2] = 0x04
    buf[3] = length & 0xff
    buf[4] = (length >> 8) & 0xff

    await this.sendRawPacket(buf)
    if (this.lastResponse === null) {
      console.warn('endWriteDeviceFlashData no response, ignore')
    } else if (this.lastResponse[2] === 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'End write flash data failed')
    }
  }

  private async checkDeviceFlashData(): Promise<void> {
    console.log('[CH585M] check device flash data')
    const total = this.firmwareBuffer.length
    let offset = 0
    let addr = this.startAddress

    while (offset < total) {
      const chunkSize = Math.min(DATA_CHUNK, total - offset)
      const chunk = this.firmwareBuffer.slice(offset, offset + chunkSize)

      const buf = new Uint8Array(PACKET_SIZE)
      buf[0] = CMD_IAP_VERIFY
      buf[1] = chunkSize + 2
      buf[2] = addr & 0xff
      buf[3] = (addr >> 8) & 0xff
      buf[4] = (addr >> 16) & 0xff
      buf[5] = (addr >> 24) & 0xff
      buf.set(chunk, 4)
      buf[4 + chunkSize] = this.calcChecksum(buf.slice(0, 4 + chunkSize))

      await this.sendRawPacket(buf)
      // 校验时允许单包超时，跳过
      if (this.lastResponse === null) {
        console.warn(`Verify packet at offset ${offset} timeout, skipping check`)
      } else if (this.lastResponse[2] === 0xff) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Verify flash data failed')
      }

      offset += chunkSize
      addr += chunkSize / 16
      this.reportProgress(50 + Math.floor((offset / total) * 45))
    }

    this.reportProgress(95)
  }

  private async endDeviceUpdate(): Promise<void> {
    console.log('[CH585M] end device update')
    const buf = new Uint8Array(PACKET_SIZE)
    buf[0] = CMD_IAP_END
    buf[1] = 0x00
    buf[2] = CMD_IAP_END

    await this.sendRawPacket(buf)
    if (!this.lastResponse) {
      await this.sendRawPacket(buf)
      if (!this.lastResponse) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'endDeviceUpdate no response after retry')
      }
    }
    if (this.lastResponse![2] !== 0x00) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Device update end failed')
    }
  }

  /**
   * 双区握手：发送 "WCH@TOOL" 命令。
   * 注意：该命令发送后设备可能会重启，因此这里不等待应答（如果收到应答建议忽略）。
   */
  private async startDeviceUpdate(): Promise<void> {
    console.log('[CH585M] start device update')
    const buf = new Uint8Array(PACKET_SIZE)
    const str = 'WCH@TOOL'
    for (let i = 0; i < str.length; i++) {
      buf[2 + i] = str.charCodeAt(i)
    }
    buf[0] = 0x5a
    buf[1] = 0x08
    buf[10] = this.calcChecksum(buf.slice(0, 10))

    // 发送底层命令，不等待应答（设备可能立刻断开）
    if (!this.updateDevice) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Update device not found')
    }
    await this.updateDevice.channel.sendOutputReport(0x00, buf)
    // 等待设备重启/进入 IAP 模式，避免后续命令竞争
    await delay(1000)
    console.log('[CH585M] startDeviceUpdate sent, device may reboot')
  }

  // ============== 辅助方法 ==============

  private calcChecksum(data: Uint8Array | ArrayLike<number>): number {
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      sum += data[i]
    }
    return sum & 0xff
  }

  /**
   * 发送原始 64 字节包并尝试接收应答。
   * 超时后 this.lastResponse 将被设为 null（不会抛出异常）。
   */
  private async sendRawPacket(data: Uint8Array): Promise<void> {
    if (!this.updateDevice) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Update device not found')
    }
    await this.updateDevice.channel.sendOutputReport(0x00, data)

    const response = await this.waitForInputReport(0x00, 3000)
    await delay(2)

    if (response === null) {
      this.lastResponse = null
      console.warn('[CH585M] No response received within timeout')
    } else {
      this.lastResponse = new Uint8Array(response.buffer, response.byteOffset, PACKET_SIZE)
    }
  }

  /**
   * 等待 reportId 的 Input Report，超时返回 null。
   */
  private waitForInputReport(reportId: number, timeout: number): Promise<DataView | null> {
    return new Promise((resolve) => {
      if (!this.updateDevice) {
        resolve(null)
        return
      }
      const timer = setTimeout(() => {
        this.updateDevice!.channel.offInputReport(handler)
        resolve(null)
      }, timeout)

      const handler = (id: number, data: DataView) => {
        if (id === reportId) {
          clearTimeout(timer)
          this.updateDevice!.channel.offInputReport(handler)
          resolve(data)
        }
      }

      this.updateDevice.channel.onInputReport(handler)
    })
  }

  private isSingleAreaDevice(): boolean {
    console.log('[CH585M] is single area device')
    const pid = this.device.channel.deviceInfo?.productId ?? 0
    const isWired = this.device.connectionType === 0xff
    return SINGLE_PID_LIST.includes(pid) && isWired
  }

  private async enterBootLoader(): Promise<void> {
    console.log('[CH585M] enter BootLoader')
    const isWired = this.device.connectionType === 0xff
    const cmd = isWired ? CMD_CH585M.firmware_update[0] : CMD_CH585M.firmware_update[1]

    if (!cmd) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'Entry BootLoader command not defined')
    }

    const payload = new Uint8Array([this.device.connectionType, cmd])
    if (!this.updateDevice) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Update device not found')
    }
    await this.updateDevice.sendAndWait(
      REPORT_ID,
      payload,
      FEATURE_REPORT_ID,
      (res) => res.getUint8(0) !== 0x02,
    )
    await delay(1000)
  }

  private reportProgress(progress: number) {
    this.device.emit(FirmwareEvents.WritedData, { progress: Math.floor(progress) })
  }
}
